/**
 * Appointment Service
 * API integration for appointment CRUD operations
 *
 * @version 2025.1.0
 * @description Service for managing medical appointments
 */

import apiClient from './apiClient';
import { ApiResponse } from '../types/common';
import {
  Appointment,
  AppointmentResponse,
  AppointmentListResponse,
  AppointmentListParams,
  AppointmentFilters,
  CreateAppointmentRequest,
  UpdateAppointmentRequest,
  AppointmentQuantityByDate,
  ApproveAppointmentRequest,
  RejectAppointmentRequest,
  PendingAppointmentsListResponse,
  ApprovalActionResult,
  LinkedClinic,
  OrdemChegadaAvailability,
  ClinicScheduleSlot,
} from '../types/appointment';

// Re-export types for convenience
export * from '../types/appointment';

/**
 * Helper to extract data from API response
 * Backend returns: { message: "...", data: {...} }
 */
const extractData = <T>(response: ApiResponse<T>): T => {
  return response.data as T;
};

class AppointmentService {
  private baseUrl = '/appointments';

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Get paginated list of appointments
   */
  async getAppointments(params: AppointmentListParams = {}): Promise<AppointmentListResponse> {
    const { page = 1, limit = 20, sortBy = 'selectedDate', sortOrder = 'DESC', ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    // Add filters
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.employeeId) queryParams.append('employeeId', filters.employeeId.toString());
    if (filters.clinicId) queryParams.append('clinicId', filters.clinicId.toString());
    if (filters.doctorId) queryParams.append('doctorId', filters.doctorId.toString());
    if (filters.confirmation) queryParams.append('confirmation', filters.confirmation);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);

    // Handle array filters (status, examType)
    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      statuses.forEach(s => queryParams.append('status', s));
    }

    if (filters.examType) {
      const examTypes = Array.isArray(filters.examType) ? filters.examType : [filters.examType];
      examTypes.forEach(t => queryParams.append('examType', t));
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { message, data: { appointments, total, page, limit, totalPages }, pagination }
    const backendData = response.data.data || response.data;
    // A paginação é irmã de `data` no corpo, não filha dele. Lida um nível abaixo, ela nunca
    // era encontrada e o contador ficava em "0 agendamentos" com a tabela cheia.
    const pagination = response.pagination ?? response.data.pagination ?? {};

    return {
      appointments: (backendData.appointments || backendData) ?? [],
      total: (pagination.total || backendData.total) ?? 0,
      page: pagination.page || backendData.page || page,
      limit: pagination.limit || backendData.limit || limit,
      totalPages: (pagination.totalPages || backendData.totalPages) ?? 0,
    };
  }

  /**
   * Get appointment by ID
   */
  async getAppointmentById(id: number): Promise<Appointment> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return extractData(response);
  }

  /**
   * Create new appointment
   */
  async createAppointment(data: CreateAppointmentRequest): Promise<{ appointment: Appointment; warnings?: string[] }> {
    console.log('📅 Creating appointment:', data);
    const response = await apiClient.post(this.baseUrl, data);
    console.log('✅ Appointment created successfully:', response.data);
    const raw = response as unknown as Record<string, unknown>;
    return {
      appointment: extractData(response),
      warnings: Array.isArray(raw['warnings']) ? raw['warnings'] as string[] : undefined,
    };
  }

  /**
   * Update appointment by ID
   */
  async updateAppointment(id: number, data: UpdateAppointmentRequest): Promise<Appointment> {
    console.log('📅 Updating appointment:', { id, data });
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    console.log('✅ Appointment updated successfully:', response.data);
    return extractData(response);
  }

  /**
   * Cancel appointment by ID
   */
  async cancelAppointment(id: number): Promise<void> {
    console.log('❌ Cancelling appointment:', id);
    await apiClient.delete(`${this.baseUrl}/${id}`);
    console.log('✅ Appointment cancelled successfully');
  }

  // ============================================================================
  // TABLE & SPECIALIZED QUERIES
  // ============================================================================

  /**
   * Get appointments table for a company
   */
  async getTable(companyId: number, filters: AppointmentFilters = {}, params: { page?: number; limit?: number } = {}): Promise<AppointmentListResponse> {
    const { page = 1, limit = 20 } = params;

    const queryParams = new URLSearchParams({
      companyId: companyId.toString(),
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      statuses.forEach(s => queryParams.append('status', s));
    }
    if (filters.examType) {
      const examTypes = Array.isArray(filters.examType) ? filters.examType : [filters.examType];
      examTypes.forEach(t => queryParams.append('examType', t));
    }
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);

    const response = await apiClient.get(`${this.baseUrl}/table?${queryParams.toString()}`);

    const backendData = response.data.data || response.data;
    const pagination = response.data.pagination ?? {};

    return {
      appointments: backendData ?? [],
      total: pagination.total ?? 0,
      page: pagination.page || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages ?? 0,
    };
  }

  /**
   * Get confirmed appointments for a company
   */
  async getConfirmed(companyId: number): Promise<AppointmentResponse[]> {
    const response = await apiClient.get(`${this.baseUrl}/confirmed?companyId=${companyId}`);
    return extractData(response);
  }

  /**
   * Get appointments by clinic
   */
  async getByClinic(clinicId: number, params: { page?: number; limit?: number } = {}): Promise<AppointmentListResponse> {
    const { page = 1, limit = 20 } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await apiClient.get(`${this.baseUrl}/clinic/${clinicId}?${queryParams.toString()}`);

    const backendData = response.data.data || response.data;
    const pagination = response.data.pagination ?? {};

    return {
      appointments: backendData ?? [],
      total: pagination.total ?? 0,
      page: pagination.page || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages ?? 0,
    };
  }

  /**
   * Get appointments by doctor
   */
  async getByDoctor(doctorId: number, params: { page?: number; limit?: number } = {}): Promise<AppointmentListResponse> {
    const { page = 1, limit = 20 } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await apiClient.get(`${this.baseUrl}/doctor/${doctorId}?${queryParams.toString()}`);

    const backendData = response.data.data || response.data;
    const pagination = response.data.pagination ?? {};

    return {
      appointments: backendData ?? [],
      total: pagination.total ?? 0,
      page: pagination.page || page,
      limit: pagination.limit || limit,
      totalPages: pagination.totalPages ?? 0,
    };
  }

  /**
   * Get appointments by employee
   */
  async getByEmployee(employeeId: number): Promise<AppointmentResponse[]> {
    const response = await apiClient.get(`${this.baseUrl}/employee/${employeeId}`);
    return extractData(response);
  }

  /**
   * Get appointment quantity by date for a clinic (for calendar)
   */
  async getQuantityByDate(clinicId: number): Promise<AppointmentQuantityByDate[]> {
    const response = await apiClient.get(`${this.baseUrl}/clinic/${clinicId}/quantity-by-date`);
    return extractData(response);
  }

  // ============================================================================
  // TOKEN-BASED OPERATIONS (PUBLIC)
  // ============================================================================

  /**
   * Confirm appointment by token (public - no auth required)
   */
  async confirmByToken(id: number, token: string): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/token/${token}`);
    return extractData(response);
  }

  /**
   * Cancel appointment by token (public - no auth required)
   */
  async cancelByToken(id: number, token: string): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/cancel/token/${token}`);
    return extractData(response);
  }

  /**
   * Request reschedule by token (public - no auth required)
   */
  async requestRescheduleByToken(id: number, token: string): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/reschedule/token/${token}`);
    return extractData(response);
  }

  /**
   * Mark as present by token (public - no auth required)
   */
  async markPresentByToken(id: number, token: string): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/present/token/${token}`);
    return extractData(response);
  }

  /**
   * Mark as absent by token (public - no auth required)
   */
  async markAbsentByToken(id: number, token: string): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/absent/token/${token}`);
    return extractData(response);
  }

  // ============================================================================
  // APPROVAL WORKFLOW OPERATIONS
  // ============================================================================

  /**
   * Get pending appointments for a professional
   */
  async getPendingForProfessional(professionalId: number, params: { page?: number; limit?: number } = {}): Promise<PendingAppointmentsListResponse> {
    const { page = 1, limit = 20 } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await apiClient.get(`${this.baseUrl}/pending/professional/${professionalId}?${queryParams.toString()}`);

    const backendData = response.data?.data || response.data;
    const pagination = response.data?.pagination ?? {};

    return {
      appointments: backendData?.appointments ?? backendData ?? [],
      total: pagination.total ?? 0,
      page: pagination.page ?? page,
      limit: pagination.limit ?? limit,
      totalPages: pagination.totalPages ?? 0,
    };
  }

  /**
   * Get pending appointments for a clinic
   */
  async getPendingForClinic(clinicId: number, params: { page?: number; limit?: number } = {}): Promise<PendingAppointmentsListResponse> {
    const { page = 1, limit = 20 } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await apiClient.get(`${this.baseUrl}/pending/clinic/${clinicId}?${queryParams.toString()}`);

    const backendData = response.data?.data || response.data;
    const pagination = response.data?.pagination ?? {};

    return {
      appointments: backendData?.appointments ?? backendData ?? [],
      total: pagination.total ?? 0,
      page: pagination.page ?? page,
      limit: pagination.limit ?? limit,
      totalPages: pagination.totalPages ?? 0,
    };
  }

  /**
   * Get all pending appointments (admin/gestor view)
   */
  async getPendingAll(params: { page?: number; limit?: number; search?: string; sortBy?: string; sortOrder?: string } = {}): Promise<PendingAppointmentsListResponse> {
    const { page = 1, limit = 20, search, sortBy = 'selectedDate', sortOrder = 'ASC' } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    if (search) queryParams.append('search', search);

    const response = await apiClient.get(`${this.baseUrl}/pending?${queryParams.toString()}`);

    const backendData = response.data?.data || response.data;
    const pagination = response.data?.pagination ?? {};

    return {
      appointments: backendData ?? [],
      total: pagination.total ?? 0,
      page: pagination.page ?? page,
      limit: pagination.limit ?? limit,
      totalPages: pagination.totalPages ?? 0,
    };
  }

  /**
   * Approve an appointment
   */
  async approveAppointment(id: number, data: ApproveAppointmentRequest = {}): Promise<ApprovalActionResult> {
    console.log('✅ Approving appointment:', { id, data });
    const response = await apiClient.post(`${this.baseUrl}/${id}/approve`, data);
    console.log('✅ Appointment approved successfully:', response.data);
    return {
      success: true,
      message: response.message || 'Agendamento aprovado com sucesso',
      appointment: extractData(response),
    };
  }

  /**
   * Reject an appointment
   */
  async rejectAppointment(id: number, data: RejectAppointmentRequest): Promise<ApprovalActionResult> {
    console.log('❌ Rejecting appointment:', { id, data });
    const response = await apiClient.post(`${this.baseUrl}/${id}/reject`, data);
    console.log('✅ Appointment rejected:', response.data);
    return {
      success: true,
      message: response.message || 'Agendamento rejeitado',
      appointment: extractData(response),
    };
  }

  // ============================================================================
  // PENDING APPROVAL COUNT
  // ============================================================================

  /**
   * Get count of appointments pending approval
   */
  async getPendingApprovalCount(): Promise<number> {
    const response = await apiClient.get(`${this.baseUrl}/pending/count`);
    const data = response.data?.data || response.data;
    return data?.count ?? 0;
  }

  // ============================================================================
  // DOCUMENT OPERATIONS
  // ============================================================================

  // ============================================================================
  // STATUS OPERATIONS
  // ============================================================================

  /**
   * Confirm appointment (change status to CONFIRMADO)
   */
  async confirmAppointment(id: number): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, {
      status: 'CONFIRMADO',
    });
    return extractData(response);
  }

  /**
   * Complete appointment (change status to CONCLUIDO)
   */
  async completeAppointment(id: number): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, {
      status: 'CONCLUIDO',
    });
    return extractData(response);
  }

  /**
   * Request reschedule (change status to REAGENDAR)
   */
  async requestReschedule(id: number): Promise<Appointment> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, {
      status: 'REAGENDAR',
    });
    return extractData(response);
  }

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; module: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Format date for display (DD/MM/YYYY)
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';

    // Check if already in DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Convert from YYYY-MM-DD to DD/MM/YYYY
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  /**
   * Format time for display (HH:MM)
   */
  formatTime(hour: string | null, minute: string | null): string {
    if (!hour) return '';
    return minute ? `${hour}:${minute}` : hour;
  }

  /**
   * Format date and time for display
   */
  formatDateTime(dateString: string, hour: string | null, minute: string | null): string {
    const date = this.formatDate(dateString);
    const time = this.formatTime(hour, minute);
    return time ? `${date} ${time}` : date;
  }

  /**
   * Check if status transition is valid
   */
  isValidStatusTransition(currentStatus: string, newStatus: string): boolean {
    const validTransitions: Record<string, string[]> = {
      PENDENTE: ['CONFIRMADO', 'CANCELADO', 'REAGENDAR'],
      AGUARDANDO_APROVACAO: ['CONFIRMADO', 'CANCELADO', 'REAGENDAR'],
      CONFIRMADO: ['CONCLUIDO', 'CANCELADO', 'REAGENDAR'],
      AGUARDANDO_LAUDOS: ['CONCLUIDO', 'CANCELADO'],
      REAGENDAR: ['AGUARDANDO_APROVACAO'],
      CONCLUIDO: [],
      CANCELADO: [],
    };

    const allowedTransitions = validTransitions[currentStatus] ?? [];
    return allowedTransitions.includes(newStatus);
  }

  /**
   * Check if appointment is active (not cancelled or completed)
   */
  isActive(status: string): boolean {
    return !['CANCELADO', 'CONCLUIDO'].includes(status);
  }

  /**
   * Check if appointment can be edited
   */
  canEdit(status: string): boolean {
    return !['CANCELADO', 'CONCLUIDO'].includes(status);
  }

  /**
   * Check if appointment can be cancelled
   */
  canCancel(status: string): boolean {
    return !['CANCELADO', 'CONCLUIDO'].includes(status);
  }

  /**
   * Get available status transitions
   */
  getAvailableTransitions(currentStatus: string): string[] {
    const validTransitions: Record<string, string[]> = {
      PENDENTE: ['CONFIRMADO', 'CANCELADO', 'REAGENDAR'],
      AGUARDANDO_APROVACAO: ['CONFIRMADO', 'CANCELADO', 'REAGENDAR'],
      CONFIRMADO: ['CONCLUIDO', 'CANCELADO', 'REAGENDAR'],
      AGUARDANDO_LAUDOS: ['CONCLUIDO', 'CANCELADO'],
      REAGENDAR: ['AGUARDANDO_APROVACAO'],
      CONCLUIDO: [],
      CANCELADO: [],
    };

    return validTransitions[currentStatus] ?? [];
  }

  /**
   * Check if appointment is pending approval
   */
  isPendingApproval(status: string): boolean {
    return status === 'AGUARDANDO_APROVACAO';
  }

  /**
   * Check if appointment was rejected
   */
  isRejected(status: string): boolean {
    return status === 'REJEITADO';
  }

  /**
   * Check if appointment is out of slot
   */
  isOutOfSlot(appointment: Appointment): boolean {
    return appointment.isOutOfSlot === true;
  }

  /**
   * Check if user can approve appointment
   */
  canApprove(status: string): boolean {
    return status === 'AGUARDANDO_APROVACAO';
  }

  /**
   * Check if user can reject appointment
   */
  canReject(status: string): boolean {
    return status === 'AGUARDANDO_APROVACAO';
  }

  // ==========================================================================
  // ORDEM DE CHEGADA (WALK-IN) METHODS
  // ==========================================================================

  /**
   * Get clinics linked to a company
   */
  async getClinicsForCompany(companyId: number): Promise<LinkedClinic[]> {
    const response = await apiClient.get(`${this.baseUrl}/clinics-for-company`, {
      params: { companyId },
    });
    return response.data;
  }

  /**
   * Get virtual clinic schedule slots for a date (Hora Marcada)
   */
  async getClinicScheduleSlots(clinicId: number, date: string): Promise<ClinicScheduleSlot[]> {
    const response = await apiClient.get(`${this.baseUrl}/clinic-schedule-slots`, {
      params: { clinicId, date },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Get Ordem de Chegada availability for a clinic on a date
   */
  async getOrdemChegadaAvailability(clinicId: number, date: string): Promise<OrdemChegadaAvailability> {
    const response = await apiClient.get(`${this.baseUrl}/ordem-chegada/availability`, {
      params: { clinicId, date },
    });
    return response.data;
  }
}

// Export singleton instance
export const appointmentService = new AppointmentService();
export default appointmentService;
